"use client";

import React, { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { signIn, useSession } from "next-auth/react";
import Button from "@/components/ui/Button";
import { BRAND_COLORS } from "@/constants/colors";

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | React.ReactNode>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Check for verification success
    if (searchParams.get("verified") === "true") {
      setSuccessMessage("Email verified successfully! You can now log in.");
    } else if (searchParams.get("error") === "invalid-token") {
      setError("Invalid verification token");
    } else if (searchParams.get("error") === "invalid-or-expired-token") {
      setError("Verification link is invalid or expired. Please request a new one.");
    } else if (searchParams.get("error") === "verification-failed") {
      setError("Email verification failed. Please try again.");
    }
  }, [searchParams]);

  useEffect(() => {
    if (status === "loading") return;

    if (session?.user) {
      router.replace("/chat-ai");
    } else {
      setIsCheckingAuth(false);
    }
  }, [session, status, router]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError("");
      
      // Use callbackUrl to specify where to redirect after sign in
      const result = await signIn("google", {
        callbackUrl: "/chat-ai",
        redirect: true,
      });

      // If we reach here, it means there was an error and redirect didn't happen
      if (result?.error) {
        setError("Failed to sign in with Google");
      }

    } catch (error) {
      setError("An error occurred during Google sign-in");
      console.error("Google sign-in error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show nothing while checking auth to prevent flash
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-100 to-orange-100">
        <div className="text-center">
          <div className="text-6xl mb-4">👵</div>
          <p className="text-amber-700">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      // First check if user exists and their verification status
      const checkResponse = await fetch('/api/auth/check-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const checkData = await checkResponse.json();
      
      // If user exists but is not verified, show specific error
      if (checkData.exists && !checkData.isVerified) {
        setError(
          <span>
            Please verify your email before logging in.{' '}
            <a 
              href={`/verify-email?email=${encodeURIComponent(email)}`}
              className="underline font-semibold hover:text-pink-700"
            >
              Resend verification email
            </a>
          </span>
        );
        setIsLoading(false);
        return;
      }

      // Proceed with sign in
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.ok) {
        router.refresh();
        router.replace("/");
      } else if (result?.error) {
        // Map NextAuth error codes to user-friendly messages
        if (result.error === "CredentialsSignin") {
          setError("Wrong email or password");
        } else {
          setError(result.error);
        }
      } else {
        setError("Wrong email or password");
      }
    } catch (err) {
      setError("An error occurred during login");
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 py-12"
        style={{
          background: "linear-gradient(135deg, #ec4899 0%, #fb923c 100%)",
        }}
      >
        <div className="max-w-lg">
          <div className="text-7xl mb-6">👵💝</div>
          <h1 className="text-5xl font-bold mb-6 text-white">
            Granny Bot
          </h1>
          <div className="space-y-4">
            <p className="text-lg leading-relaxed text-white opacity-95">
              Your caring AI companion bringing comfort, positivity, and traditional wisdom into the digital world.
            </p>
            <p className="text-lg leading-relaxed text-white opacity-90">
              Talk to Granny about your feelings, get trusted home remedies, and receive compassionate guidance whenever you need it.
            </p>
            <p className="text-lg font-semibold leading-relaxed text-white">
              Let Granny be your digital grandmother, always here to listen and care. 🤗
            </p>
          </div>

          {/* Decorative Elements */}
          <div className="mt-12 flex gap-4">
            <div className="w-16 h-1 rounded-full bg-white" />
            <div className="w-8 h-1 rounded-full bg-white opacity-60" />
            <div className="w-4 h-1 rounded-full bg-white opacity-30" />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div
        className="flex-1 flex items-center justify-center px-6 py-12"
        style={{ backgroundColor: BRAND_COLORS.background.primary }}
      >
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="text-6xl mb-4">👵💝</div>
            <h1 className="text-4xl font-bold mb-4 text-amber-900">
              Granny Bot
            </h1>
            <p className="text-base text-amber-700">
              Your caring AI companion
            </p>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2 text-amber-900">
              Welcome back, beta! 🙏
            </h2>
            <p className="text-amber-700">
              Sign in to continue chatting with Granny
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold mb-2"
                style={{ color: BRAND_COLORS.text.primary }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: BRAND_COLORS.border.default,
                  color: BRAND_COLORS.text.primary,
                  backgroundColor: BRAND_COLORS.background.primary,
                }}
                placeholder="you@example.com"
                disabled={isLoading}
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold mb-2"
                style={{ color: BRAND_COLORS.text.primary }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: BRAND_COLORS.border.default,
                  color: BRAND_COLORS.text.primary,
                  backgroundColor: BRAND_COLORS.background.primary,
                }}
                placeholder="••••••••"
                disabled={isLoading}
              />
            </div>

            {/* Success Message */}
            {successMessage && (
              <div
                className="p-4 rounded-lg text-sm flex items-center gap-2"
                style={{
                  backgroundColor: "#d1fae5",
                  color: "#065f46",
                }}
              >
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div
                className="p-4 rounded-lg text-sm flex items-center gap-2"
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                }}
              >
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="w-full py-3 text-base font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2" style={{ borderColor: BRAND_COLORS.border.default }} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4" style={{ backgroundColor: BRAND_COLORS.background.primary }}>
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google Sign-In Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 rounded-lg transition-all hover:shadow-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                borderColor: BRAND_COLORS.border.default,
                backgroundColor: BRAND_COLORS.background.primary,
                color: BRAND_COLORS.text.primary,
              }}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="font-semibold">
                {isLoading ? "Signing in with Google..." : "Continue with Google"}
              </span>
            </button>

            {/* Register Link */}
            <div className="text-center pt-4">
              <p className="text-sm text-amber-700">
                Don't have an account?{" "}
                <Link
                  href="/register"
                  className="font-semibold text-pink-600 hover:text-pink-700 transition-colors underline"
                >
                  Create one here
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-100 to-orange-100">
        <div className="text-center">
          <div className="text-6xl mb-4">👵</div>
          <p className="text-amber-700">Loading...</p>
        </div>
      </div>
    }>
      <LoginContent />
    </Suspense>
  );
}
